import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Progress } from '@client/components/_ui/feedback/Progress';
import { Space } from '@client/components/_ui/layout/Space';
import { tokenValues } from '@client/design-system/__generated__';
import { useConnectionTest } from '@client/hooks/system/useConnectionTest';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { useAppStateStore } from '@client/stores/AppStateStore';
import type { NetworkErrorDetails } from '@client/utils/errors/network-error-detection';
import { Card, Collapse, Spin } from 'antd';
import {
    Activity,
    AlertCircle,
    CheckCircle,
    ChevronDown,
    Clock,
    Copy,
    Globe,
    RefreshCw,
    Server,
    WifiOff,
    X,
    Zap,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

export interface NetworkStatusDialogProps {
    errorDetails: NetworkErrorDetails;
    endpoint?: string;
    timestamp?: Date;
    onRetry?: () => void;
    onClose?: () => void;
    retrying?: boolean;
    retryAttempt?: number;
    maxRetries?: number;
}

export function NetworkStatusDialog({
    errorDetails,
    endpoint,
    timestamp,
    onRetry,
    onClose,
    retrying = false,
    retryAttempt = 0,
    maxRetries = 3,
}: NetworkStatusDialogProps) {
    const [copied, setCopied] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(false);
    const { testing, lastResult, testConnection } = useConnectionTest();
    const connectionState = useAppStateStore((state) => state.connectionState);
    const { formatDateTime } = useFormatter();

    // Auto-expand troubleshooting section when there's an error
    const [troubleshootingExpanded, setTroubleshootingExpanded] = useState(true);

    // Retry countdown
    const [retryCountdown, setRetryCountdown] = useState<number | null>(null);

    useEffect(() => {
        if (retrying && errorDetails.retryDelay) {
            setRetryCountdown(Math.ceil(errorDetails.retryDelay / 1000));
            const interval = setInterval(() => {
                setRetryCountdown((prev) => {
                    if (prev === null || prev <= 1) {
                        clearInterval(interval);
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [retrying, errorDetails.retryDelay]);

    const getIconForErrorType = () => {
        switch (errorDetails.type) {
            case 'offline':
                return <WifiOff className="text-red-500 dark:text-red-400" size={32} />;
            case 'timeout':
                return <Clock className="text-warning" size={32} />;
            case 'server_error':
                return <Server className="text-red-500 dark:text-red-400" size={32} />;
            case 'dns':
            case 'network_failed':
                return <Globe className="text-red-500 dark:text-red-400" size={32} />;
            default:
                return <AlertCircle className="text-red-500 dark:text-red-400" size={32} />;
        }
    };

    const getConnectionQualityBadge = () => {
        const quality = errorDetails.connectionQuality || connectionState.quality;

        const badges = {
            good: <StatusBadge module="state" status="CONNECTED" />,
            poor: <StatusBadge module="state" status="DEGRADED" customLabel="Poor Connection" />,
            offline: <StatusBadge module="state" status="OFFLINE" />,
        };

        return badges[quality];
    };

    const copyErrorDetails = async () => {
        // curium:allow-date-format — support report copied to the clipboard, ISO order with the zone name
        const reportTime = timestamp ? DateTime.fromJSDate(timestamp).toFormat('yyyy-MM-dd HH:mm:ss ZZZZ') : '';
        const details = `
Network Status Report
====================
Error Type: ${errorDetails.type}
Title: ${errorDetails.title}
Message: ${errorDetails.userMessage}
Technical Details: ${errorDetails.technicalMessage}
${endpoint ? `Endpoint: ${endpoint}` : ''}
${reportTime ? `Time: ${reportTime}` : ''}

Connection Status
=================
Quality: ${connectionState.quality}
Consecutive Failures: ${connectionState.consecutiveFailures}
${connectionState.lastChecked ? `Last Checked: ${formatDateTime(connectionState.lastChecked, 'yyyy-MM-dd HH:mm:ss')}` : ''}
Browser Online Status: ${navigator.onLine ? 'Online' : 'Offline'}

${
    lastResult
        ? `
Last Connection Test
====================
Success: ${lastResult.success ? 'Yes' : 'No'}
${lastResult.latency ? `Latency: ${lastResult.latency}ms` : ''}
Time: ${formatDateTime(lastResult.timestamp, 'yyyy-MM-dd HH:mm:ss')}
${lastResult.error ? `Error: ${lastResult.error}` : ''}
`
        : ''
}

System Information
==================
User Agent: ${navigator.userAgent}
${(navigator as any).connection?.effectiveType ? `Connection Type: ${(navigator as any).connection.effectiveType}` : ''}
        `.trim();

        try {
            await navigator.clipboard.writeText(details);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy error details:', error);
        }
    };

    const handleTestConnection = async () => {
        await testConnection();
    };

    return (
        <div className="space-y-4 py-2">
            {/* Main Error Card with integrated status */}
            <Card className="border-red-200 bg-red-50/30">
                <div className="space-y-3">
                    {/* Header with icon, title, and badges */}
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">{getIconForErrorType()}</div>
                        <div className="min-w-0 flex-1">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <h3 className="font-semibold text-foreground text-lg">{errorDetails.title}</h3>
                                {getConnectionQualityBadge()}
                            </div>
                            <p className="text-muted-foreground text-sm leading-relaxed">{errorDetails.userMessage}</p>

                            {/* Connection info */}
                            <div className="mt-3 space-y-1">
                                {connectionState.consecutiveFailures > 0 && (
                                    <p className="text-warning-foreground text-xs">
                                        {connectionState.consecutiveFailures} consecutive failure
                                        {connectionState.consecutiveFailures > 1 ? 's' : ''} detected
                                    </p>
                                )}
                                {timestamp && (
                                    <p className="text-subtle-foreground text-xs">
                                        Occurred {DateTime.fromJSDate(timestamp).toRelative()}
                                    </p>
                                )}
                                {connectionState.lastChecked && (
                                    <p className="text-subtle-foreground text-xs">
                                        Last checked: {DateTime.fromJSDate(connectionState.lastChecked).toRelative()}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Test Connection Button and Results */}
                    <div className="border-separator border-t pt-2">
                        <Button
                            size="small"
                            type="secondary"
                            icon={testing ? <Spin size="small" /> : <Activity />}
                            onClick={handleTestConnection}
                            disabled={testing}
                            block
                        >
                            Test Connection
                        </Button>

                        {/* Test Result */}
                        {lastResult && (
                            <div className="mt-3 rounded-lg border border-separator bg-card p-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {lastResult.success ? (
                                            <>
                                                <CheckCircle className="text-green-600 dark:text-green-400" size={16} />
                                                <span className="font-medium text-green-700 text-sm dark:text-green-400">
                                                    Connection Successful
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <X className="text-red-600 dark:text-red-400" size={16} />
                                                <span className="font-medium text-red-700 text-sm dark:text-red-400">
                                                    Connection Failed
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    {lastResult.latency && (
                                        <div className="flex items-center gap-1 text-muted-foreground text-xs">
                                            <Zap size={12} />
                                            <span>{lastResult.latency}ms</span>
                                        </div>
                                    )}
                                </div>
                                {lastResult.error && (
                                    <p className="mt-2 text-red-600 text-xs dark:text-red-400">{lastResult.error}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Retry Progress */}
            {retrying && (
                <Card className="border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-900/20">
                    <div className="flex items-start gap-3">
                        <RefreshCw
                            className="mt-0.5 flex-shrink-0 animate-spin text-blue-600 dark:text-blue-400"
                            size={18}
                        />
                        <div className="min-w-0 flex-1">
                            <p className="mb-2 font-medium text-blue-900 text-sm dark:text-blue-100">
                                Retrying connection...
                                {retryAttempt > 0 && ` (Attempt ${retryAttempt} of ${maxRetries})`}
                            </p>
                            {retryCountdown !== null && (
                                <>
                                    <p className="mb-2 text-blue-700 text-xs dark:text-blue-300">
                                        Next attempt in {retryCountdown}s
                                    </p>
                                    <Progress
                                        percent={
                                            ((errorDetails.retryDelay! / 1000 - retryCountdown) /
                                                (errorDetails.retryDelay! / 1000)) *
                                            100
                                        }
                                        showInfo={false}
                                        strokeColor={tokenValues.color.info[600]}
                                        size="small"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Troubleshooting Section */}
            {errorDetails.suggestions.length > 0 && (
                <Collapse
                    activeKey={troubleshootingExpanded ? ['troubleshooting'] : []}
                    onChange={(keys) => setTroubleshootingExpanded(keys.includes('troubleshooting'))}
                    ghost
                    items={[
                        {
                            key: 'troubleshooting',
                            label: (
                                <div className="flex items-center gap-2 font-medium text-muted-foreground text-sm">
                                    <AlertCircle size={16} />
                                    <span>Troubleshooting Steps</span>
                                </div>
                            ),
                            children: (
                                <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/30">
                                    <p className="mb-3 font-medium text-blue-900 text-sm dark:text-blue-100">
                                        What you can do:
                                    </p>
                                    <ul className="space-y-2 text-blue-800 text-sm dark:text-blue-200">
                                        {errorDetails.suggestions.map((suggestion, index) => (
                                            <li key={index} className="flex items-start gap-2">
                                                <span className="mt-0.5 flex-shrink-0 font-bold text-blue-600 dark:text-blue-400">
                                                    {index + 1}.
                                                </span>
                                                <span>{suggestion}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        },
                    ]}
                />
            )}

            {/* Technical Details (collapsible) */}
            <Collapse
                ghost
                items={[
                    {
                        key: '1',
                        label: (
                            <div className="flex items-center gap-2 font-medium text-muted-foreground text-sm">
                                <ChevronDown
                                    className={`transition-transform ${detailsExpanded ? 'rotate-180' : ''}`}
                                    size={16}
                                />
                                <span>Technical Details</span>
                            </div>
                        ),
                        children: (
                            <div className="space-y-3 rounded-lg border border-separator bg-muted p-4">
                                <div>
                                    <p className="mb-1 font-medium text-subtle-foreground text-xs">Error Message</p>
                                    <p className="break-all font-mono text-muted-foreground text-sm">
                                        {errorDetails.technicalMessage}
                                    </p>
                                </div>

                                {endpoint && (
                                    <div>
                                        <p className="mb-1 font-medium text-subtle-foreground text-xs">Endpoint</p>
                                        <p className="break-all font-mono text-muted-foreground text-sm">{endpoint}</p>
                                    </div>
                                )}

                                {timestamp && (
                                    <div>
                                        <p className="mb-1 font-medium text-subtle-foreground text-xs">Timestamp</p>
                                        <p className="text-muted-foreground text-sm">
                                            {formatDateTime(timestamp, 'dd/MM/yyyy HH:mm:ss')}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <p className="mb-1 font-medium text-subtle-foreground text-xs">Browser Status</p>
                                    <p className="text-muted-foreground text-sm">
                                        {navigator.onLine ? 'Reports online' : 'Reports offline'}
                                    </p>
                                </div>

                                {(navigator as any).connection?.effectiveType && (
                                    <div>
                                        <p className="mb-1 font-medium text-subtle-foreground text-xs">
                                            Connection Type
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            {(navigator as any).connection.effectiveType}
                                        </p>
                                    </div>
                                )}

                                <Button
                                    size="small"
                                    type="secondary"
                                    icon={copied ? <CheckCircle /> : <Copy />}
                                    onClick={copyErrorDetails}
                                    className="mt-2 w-full"
                                >
                                    {copied ? 'Copied!' : 'Copy Full Report'}
                                </Button>
                            </div>
                        ),
                    },
                ]}
                onChange={(keys) => setDetailsExpanded(keys.length > 0)}
            />

            {/* Action buttons */}
            <div className="flex items-center justify-between gap-2 border-separator border-t pt-2">
                <div className="text-subtle-foreground text-xs">
                    {errorDetails.isRetryable && !retrying && 'You can try again or close this dialogue'}
                </div>
                <Space size="small">
                    {onClose && (
                        <Button type="secondary" icon={<X />} onClick={onClose} disabled={retrying}>
                            Close
                        </Button>
                    )}

                    {errorDetails.isRetryable && onRetry && (
                        <Button
                            type="primary"
                            icon={<RefreshCw className={retrying ? 'animate-spin' : ''} />}
                            onClick={onRetry}
                            loading={retrying}
                            disabled={retrying}
                        >
                            {retrying ? 'Retrying...' : retryCountdown ? `Retry (${retryCountdown}s)` : 'Try Again'}
                        </Button>
                    )}
                </Space>
            </div>
        </div>
    );
}
